import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Plus, Trash2, Search, RotateCcw, Pencil, AlertTriangle, X, Smartphone, Eye, LayoutGrid, List } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { Skeleton } from '../../../components/ui/skeleton';
import ThemeToggle from '../../../components/ThemeToggle';
import { usePermissions } from '../../../hooks/usePermissions';

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, device: null });
  const [editDialog, setEditDialog] = useState({ isOpen: false, device: null });
  const [editFormData, setEditFormData] = useState({
    device_type_id: '',
    device_name: '',
    imei_number: '',
    gps_card_no: '',
    gps_serial_no: '',
    sim_card: '',
    device_status: 'In-Pool',
    notes: ''
  });
  const { get, put, delete: deleteApi } = useApi();
  const navigate = useNavigate();
  const { can, PERMISSIONS } = usePermissions();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [isLoadingDeviceTypes, setIsLoadingDeviceTypes] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    fetchDeviceTypes();
  }, []);

  // Set default view mode based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setViewMode(isMobile ? 'grid' : 'table');
    };

    // Set initial view mode
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleRefresh = () => {
    fetchDevices();
  };

  const handleViewDevice = (device) => {
    const deviceId = device._id || device.device_id;
    navigate(`/admin/devices/${deviceId}`);
  };

  const fetchDeviceTypes = async () => {
    setIsLoadingDeviceTypes(true);
    try {
      const response = await get('/device-types?perPage=1000');
      if (response?.success && Array.isArray(response?.data)) {
        setDeviceTypes(response.data);
      } else if (Array.isArray(response)) {
        setDeviceTypes(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setDeviceTypes(response.data);
      } else {
        setDeviceTypes([]);
      }
    } catch (error) {
      setDeviceTypes([]);
    } finally {
      setIsLoadingDeviceTypes(false);
    }
  };

  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      
      const response = await get('/devices');
      
      
      // Handle the API response structure
      if (response?.success && Array.isArray(response?.data)) {
        setDevices(response.data);
      } else if (Array.isArray(response)) {
        setDevices(response);
      } else {
        setDevices([]);
      }
      
    } catch (error) {
      
      let errorMessage = 'An error occurred while fetching devices. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found. Please check the server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteDialog = (device) => {
    setDeleteDialog({ isOpen: true, device });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, device: null });
  };

  const openEditDialog = (device) => {
    setEditDialog({ isOpen: true, device });
    setEditFormData({
      device_type_id: device.device_type_id || device.deviceTypeId || device.deviceType?._id || '',
      device_name: device.deviceName || device.device_name || '',
      imei_number: device.imeiNumber || device.imei_number || '',
      gps_card_no: device.gpsCardNo || device.gps_card_no || '',
      gps_serial_no: device.gpsSerialNo || device.gps_serial_no || '',
      sim_card: device.simCard || device.sim_card || '',
      device_status: device.deviceStatus || device.device_status || 'In-Pool',
      notes: device.notes || ''
    });
  };

  const closeEditDialog = () => {
    setEditDialog({ isOpen: false, device: null });
    setEditFormData({
      device_type_id: '',
      device_name: '',
      imei_number: '',
      gps_card_no: '',
      gps_serial_no: '',
      sim_card: '',
      device_status: 'In-Pool',
      notes: ''
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateDevice = async () => {
    if (!editDialog.device) return;
    
    try {
      const apiPayload = {
        ...editFormData
      };
      
      const deviceId = editDialog.device._id || editDialog.device.device_id;
      const response = await put(`/devices/${deviceId}`, apiPayload);
      
      
      // Use the response data if available, otherwise map the form data to camelCase
      let updatedDeviceData;
      if (response?.data) {
        // If API returns updated device data, use it
        updatedDeviceData = response.data;
      } else {
        // Otherwise, map form data to camelCase format to match API structure
        updatedDeviceData = {
          deviceName: editFormData.device_name,
          imeiNumber: editFormData.imei_number,
          gpsCardNo: editFormData.gps_card_no,
          gpsSerialNo: editFormData.gps_serial_no,
          simCard: editFormData.sim_card,
          deviceStatus: editFormData.device_status,
          notes: editFormData.notes
        };
      }
      
      setDevices(prevDevices => 
        prevDevices.map(device => 
          (device._id || device.device_id) === deviceId 
            ? { ...device, ...updatedDeviceData }
            : device
        )
      );
      
      toast.success('Device updated successfully');
      closeEditDialog();
    } catch (error) {
      
      let errorMessage = 'An error occurred while updating the device. Please try again.';

      if (Array.isArray(error.response?.data?.errors) && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors
          .map((err) => err.msg || err.message || `${err.path || 'field'} is invalid`)
          .join(', ');
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteDevice = async () => {
    if (!deleteDialog.device) return;
    
    try {
      const deviceId = deleteDialog.device._id || deleteDialog.device.device_id;
      
      const response = await deleteApi(`/devices/${deviceId}`);
      
      // Remove the device from the local devices array
      setDevices(prevDevices => 
        prevDevices.filter(device => (device._id || device.device_id) !== deviceId)
      );
      
      toast.success('Device deleted successfully');
      closeDeleteDialog();
    } catch (error) {
      
      let errorMessage = 'An error occurred while deleting the device. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Filter devices based on search term
  const filteredDevices = devices.filter(device =>
    (device.deviceName || device.device_name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (device.imeiNumber || device.imei_number)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (device.gpsCardNo || device.gps_card_no)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (device.deviceStatus || device.device_status)?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDevices = filteredDevices.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow className="h-8">
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
        <Skeleton className="h-4 w-28" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden lg:table-cell">
        <Skeleton className="h-4 w-28" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-6 w-16 rounded-full" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <h1 className="text-xl sm:text-2xl font-bold">Devices</h1>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
          {/* Search Input */}
          <div className="relative max-w-sm">
            <Input
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8 border-0 bg-muted/50"
            />
          </div>
          
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          
          {/* Grid/Table Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            {viewMode === 'table' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
            <span className="hidden sm:inline">{viewMode === 'table' ? 'Grid' : 'Table'}</span>
          </Button>
        </div>
      </div>

      {/* Table or Grid View */}
      {viewMode === 'table' ? (
      <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
        <Table className="text-xs sm:text-sm min-w-full border-1 border-muted-foreground/20">
          <TableHeader className="bg-muted/70 border-1 border-muted-foreground/20">
            <TableRow className="h-8 bg-muted/50">
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px]">Date</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[140px] sm:min-w-[160px]">Device Name</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[140px] hidden sm:table-cell">IMEI</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px] hidden md:table-cell">GPS Card</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[140px] hidden lg:table-cell">SIM Card</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Status</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} />
              ))
            ) : (
              currentDevices.map((device) => (
                <TableRow key={device._id || device.device_id} className="h-8">
                  <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                    <span className="text-xs sm:text-sm ">
                      {(device.createdOn || device.activate_date) ? 
                        new Date(device.createdOn || device.activate_date).toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">
                      {device.deviceName || device.device_name}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                    <span className="text-xs sm:text-sm  truncate max-w-[100px] sm:max-w-[120px]">
                      {device.imeiNumber || device.imei_number}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
                    <span className="text-xs sm:text-sm ">
                      {device.gpsCardNo || device.gps_card_no}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2  text-xs sm:text-sm hidden lg:table-cell">
                    {device.simCard || device.sim_card || 'N/A'}
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className={`px-1 py-0.5 rounded text-xs sm:text-sm font-medium ${
                      (device.deviceStatus || device.device_status) === 'Assigned' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      (device.deviceStatus || device.device_status) === 'In-Pool' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' :
                      (device.deviceStatus || device.device_status) === 'In-Repair' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      (device.deviceStatus || device.device_status) === 'Maintenance' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100' :
                      (device.deviceStatus || device.device_status) === 'Terminated' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' :
                      (device.deviceStatus || device.device_status) === 'Deleted' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                    }`}>
                      {device.deviceStatus || device.device_status || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      {can(PERMISSIONS.VIEW_DEVICE_DETAILS) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleViewDevice(device)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 "
                          title="View Details"
                        >
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.EDIT_DEVICE) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(device)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0"
                          title="Edit Device"
                        >
                          <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.DELETE_DEVICE) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDeleteDialog(device)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                          title="Delete Device"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading ? (
            /* Skeleton Grid */
            Array.from({ length: 8 }).map((_, index) => (
              <div key={`skeleton-${index}`} className="bg-card text-card-foreground rounded-sm overflow-x-auto border-1 border-muted-foreground/20">
                <div className="p-4 space-y-3">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                </div>
              </div>
            ))
          ) : currentDevices.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Smartphone className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No devices found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first device'}
              </p>
            </div>
          ) : (
            currentDevices.map((device) => (
              <div 
                key={device._id || device.device_id} 
                className="bg-card text-card-foreground rounded-sm overflow-hidden border-1 border-muted-foreground/20 hover:shadow-lg transition-all duration-300 hover:border-primary/20 group bg-muted/50"
              >
                <div className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">Device Name</p>
                      <p className="text-sm font-semibold truncate">
                        {device.deviceName || device.device_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {can(PERMISSIONS.VIEW_DEVICE_DETAILS) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleViewDevice(device)}
                          className="h-8 w-8 p-0"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.EDIT_DEVICE) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(device)}
                          className="h-8 w-8 p-0"
                          title="Edit Device"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.DELETE_DEVICE) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDeleteDialog(device)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          title="Delete Device"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* IMEI Number */}
                  {(device.imeiNumber || device.imei_number) && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">IMEI</p>
                      <p className="text-sm truncate">
                        {device.imeiNumber || device.imei_number}
                      </p>
                    </div>
                  )}

                  {/* GPS Card Number */}
                  {(device.gpsCardNo || device.gps_card_no) && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">GPS Card</p>
                      <p className="text-sm truncate">
                        {device.gpsCardNo || device.gps_card_no}
                      </p>
                    </div>
                  )}

                  {/* SIM Card */}
                  {(device.simCard || device.sim_card) && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">SIM Card</p>
                      <p className="text-sm truncate">
                        {device.simCard || device.sim_card}
                      </p>
                    </div>
                  )}

                  {/* Date & Status */}
                  <div className="flex items-center justify-between pt-2 border-t border-muted-foreground/10">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {(device.createdOn || device.activate_date) ? 
                          new Date(device.createdOn || device.activate_date).toLocaleDateString('en-GB') : 'N/A'}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      (device.deviceStatus || device.device_status) === 'Assigned' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      (device.deviceStatus || device.device_status) === 'In-Pool' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' :
                      (device.deviceStatus || device.device_status) === 'In-Repair' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                    }`}>
                      {device.deviceStatus || device.device_status || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-48" />
            <div className="flex items-center gap-2 flex-wrap">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-12" />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Showing page {currentPage} of {totalPages} | Total: {filteredDevices.length} devices
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="hover:bg-muted/50"
              >
                Previous
              </Button>
              {/* Show page 1 if not in range */}
              {currentPage > 2 && totalPages > 3 && (
                <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                    className={`${
                      currentPage === 1 
                        ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
              >
                1
              </Button>
                  {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                </>
              )}
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return page;
              })
              .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
              .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
              .map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`${
                    currentPage === page 
                      ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  {page}
                </Button>
              ))}
              {/* Show last page if not in range */}
              {currentPage < totalPages - 2 && totalPages > 3 && (
                <>
                  {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                <Button
                  variant="outline"
                  size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    className={`${
                      currentPage === totalPages 
                        ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                >
                    {totalPages}
                </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="hover:bg-muted/50"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Device</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete the device{' '}
                <span className="font-semibold text-foreground">
                  "{deleteDialog.device?.deviceName || deleteDialog.device?.device_name}"
                </span>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This will permanently remove the device and all associated data.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={closeDeleteDialog} className="px-4 py-2">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteDevice}
                className="px-4 py-2 bg-red-600 hover:bg-red-700"
              >
                Delete Device
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Device Sheet */}
      <Sheet open={editDialog.isOpen} onOpenChange={(open) => !open && closeEditDialog()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-3 flex flex-col h-full">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Smartphone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Edit Device
            </SheetTitle>
            <SheetDescription>
              Update device information
            </SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto space-y-1 mt-3">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Model Name
                </label>
                <Select
                  value={editFormData.device_type_id}
                  onValueChange={(value) => handleEditFormChange('device_type_id', value)}
                  disabled={isLoadingDeviceTypes}
                >
                  <SelectTrigger className="w-full text-foreground border-1 border-muted-foreground/20 bg-muted/50">
                    <SelectValue placeholder={isLoadingDeviceTypes ? 'Loading...' : 'Select model'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {deviceTypes.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No device types available</div>
                    ) : (
                      deviceTypes.map((type) => {
                        const typeId = type._id || type.id;
                        const deviceName = type.name || 'N/A';
                        const modelName = type.modelName || type.model_name || 'N/A';
                        return (
                          <SelectItem key={typeId} value={typeId}>
                            {deviceName} - {modelName}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Device Name
                </label>
                <Input
                  value={editFormData.device_name}
                  onChange={(e) => handleEditFormChange('device_name', e.target.value)}
                  placeholder="Enter device name"
                  className="w-full text-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  IMEI Number
                </label>
                <Input
                  type="tel"
                  value={editFormData.imei_number}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers and limit to 20 characters
                    if (/^[0-9]*$/.test(value) && value.length <= 20) {
                      handleEditFormChange('imei_number', value);
                    }
                  }}
                  placeholder="Enter IMEI number (max 20 digits)"
                  maxLength={20}
                  className="w-full text-foreground border-1 border-muted-foreground/20"
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  GPS Card Number
                </label>
                <Input
                  value={editFormData.gps_card_no}
                  onChange={(e) => handleEditFormChange('gps_card_no', e.target.value)}
                  placeholder="Enter GPS card number"
                  className="w-full text-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  GPS Serial Number
                </label>
                <Input
                  value={editFormData.gps_serial_no}
                  onChange={(e) => handleEditFormChange('gps_serial_no', e.target.value)}
                  placeholder="Enter GPS serial number"
                  className="w-full text-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  SIM Card
                </label>
                <Input
                  value={editFormData.sim_card}
                  onChange={(e) => handleEditFormChange('sim_card', e.target.value)}
                  placeholder="Enter SIM card number"
                  className="w-full text-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={editFormData.device_status}
                  onChange={(e) => handleEditFormChange('device_status', e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-foreground bg-muted/50 focus:outline-none border-1 border-muted-foreground/20"
                >
                  <option value="Assigned">Assigned</option>
                  <option value="In-Repair">In-Repair</option>
                  <option value="In-Pool">In-Pool</option>
                  <option value="Maintenance">Maintenance</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => handleEditFormChange('notes', e.target.value)}
                  placeholder="Enter notes (optional)"
                  rows="3"
                  className="w-full px-3 py-2 rounded-md text-foreground bg-muted/50 focus:outline-none text-sm border-1 border-muted-foreground/20"
                />
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={closeEditDialog}
              className="px-6 py-2 min-w-[80px] bg-background border-border text-foreground hover:bg-muted border-1 border-muted-foreground/20"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUpdateDevice}
              className="px-6 py-2 min-w-[120px] bg-background border-border text-foreground hover:bg-muted"
            >
              Update Device
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
